import { scaleBand, ScaleBand, scaleLinear, ScaleLinear } from "d3-scale";
// import { scaleBand, ScaleBand, scaleLinear, ScaleLinear, ScaleOrdinal, ScaleTime } from "d3-scale";
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

// Define the context type
interface LayerCakeContextPropsType {
  width: number;
  height: number;
  maxHeight?: number;
  domain: [number, number];
  range: [number, number];
  points?: any[];
  active?: any;
  setActive?: (any) => void;
  padding: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  }
}

// type Scale = ScaleLinear<number, number, number> | ScaleOrdinal<string, number, never> | ScaleTime<number, number, never>;
interface LayerCakeContextType extends LayerCakeContextPropsType {
  domainScale: ScaleLinear<number, number, number>;
  domainBand: ScaleBand<string>;
  rangeScale: ScaleLinear<number, number, number>;
}
// Create the context with a default value
const LayerCakeContext = createContext<LayerCakeContextType | undefined>(undefined);

interface LayerCakeProps extends LayerCakeContextPropsType{
  generateDomainScale?: ({width, domain}: { width: number, domain: [number, number] }) => ScaleLinear<number, number, number>;
  generateRangeScale?: ({height, range}: { height: number, range: [number, number] }) => ScaleLinear<number, number, number> ;
  children: React.ReactNode;
}

function defaultDomainGenerator({ width, domain }: { width: number, domain: [number, number] }): ScaleLinear<number, number, number> {
  return scaleLinear().domain(domain).range([0, width]);
}

function defaultRangeGenerator({ height, range }: { height: number, range: [number, number] }): ScaleLinear<number, number, number> {
  return scaleLinear().domain(range).range([height, 0]);
}

export default function LayerCake({
  children,
  width,
  maxHeight,
  height,
  range,
  domain,
  points,
  generateDomainScale = defaultDomainGenerator,
  generateRangeScale = defaultRangeGenerator,
  padding = {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
}: LayerCakeProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width, height });
  const [active, setActive] = useState(null);

  const domainScale = useMemo(() => {
    return generateDomainScale({ width: dimensions.width - padding.left - padding.right, domain });
  }, [dimensions.width, padding.left, padding.right, domain])

  const rangeScale = useMemo(() => {
    return generateRangeScale({ height: dimensions.height - padding.top - padding.bottom, range });
  }, [dimensions.height, padding.top, padding.bottom, range])

  const domainBand = useMemo(() => {
    const p = points.map((d) => d.label)
    return scaleBand()
            .domain(p)
            .range([0, dimensions.width - padding.left - padding.right])
            .paddingInner(0.1)
            .paddingOuter(0);
  }, [points, dimensions.width, padding.left, padding.right]);

  useEffect(() => {
    const updateDimensions = () => {
      if (wrapperRef.current) {
        const { width:newWidth, height:newHeight } = wrapperRef.current.getBoundingClientRect();
        setDimensions({ width: newWidth || width, height: newHeight || height });
      }
    };

    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <LayerCakeContext.Provider
      value={{
        width: dimensions.width,
        height: dimensions.height,
        maxHeight,
        domain,
        domainScale,
        domainBand,
        range,
        rangeScale,
        padding,
        active,
        setActive
      }}
    >
      <div ref={wrapperRef} className="relative" style={{maxHeight: maxHeight || "auto"}}>
        {children}
      </div>
    </LayerCakeContext.Provider>
  );
}

export function useLayerCake() {
  const context = useContext(LayerCakeContext);
  if (context === undefined) {
    throw new Error('useLayerCake must be used within a LayerCakeProvider');
  }
  return context;
}
